import { useEffect, useRef, useState } from 'react';
import { Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import * as api from '../lib/api';

const SESSION_KEY = 'mipisto_signup_recovery_shown';
const ARM_DELAY_MS = 4000; // ignore a mouse-leave right as the page loads

/**
 * Exit-intent email capture shown while someone is filling out the signup
 * form but leaves before finishing. Entirely independent of real
 * registration: it only ever calls POST /api/leads (source:
 * "signup_abandon") — never creates a user, never touches passwords,
 * tokens, or the auth flow in any way. Shows at most once per session.
 *
 * `active` gates the whole thing (only armed while the register tab is open
 * and no other modal is up); `prefillEmail` is whatever the visitor already
 * typed into the real form, purely as a convenience so they don't retype it.
 */
export default function SignupRecoveryModal({ active, prefillEmail }) {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const armedRef = useRef(false);
    const shownRef = useRef(false);
    // Keeps the latest typed email available to the mouseout listener below
    // without re-subscribing it on every keystroke (the effect only depends
    // on `active`, so a plain closure over the prop would go stale).
    const prefillRef = useRef(prefillEmail);
    useEffect(() => { prefillRef.current = prefillEmail; }, [prefillEmail]);

    useEffect(() => {
        if (!active) return undefined;

        let alreadyShown = false;
        try {
            alreadyShown = !!sessionStorage.getItem(SESSION_KEY);
        } catch (_) { /* private mode / storage disabled */ }
        if (alreadyShown) return undefined;

        const armTimer = setTimeout(() => { armedRef.current = true; }, ARM_DELAY_MS);

        const onMouseOut = (e) => {
            if (!armedRef.current || shownRef.current) return;
            if (e.clientY > 0 || e.relatedTarget) return; // only cursor leaving through the top
            shownRef.current = true;
            try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) {}
            setEmail(prefillRef.current || '');
            setOpen(true);
        };

        document.addEventListener('mouseout', onMouseOut);
        return () => {
            clearTimeout(armTimer);
            document.removeEventListener('mouseout', onMouseOut);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = email.trim();
        if (!trimmed) return;
        setLoading(true);
        try {
            await api.captureLead({ email: trimmed, source: 'signup_abandon' });
            toast.success('¡Listo! Te ayudamos a terminar tu cuenta cuando quieras.');
            setOpen(false);
        } catch (_) {
            toast.error('No pudimos guardar tu correo. Intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                data-testid="signup-recovery-modal"
                className="bg-slate-900/95 border-slate-700 rounded-2xl max-w-sm text-center"
            >
                <div className="w-12 h-12 rounded-xl bg-violet-500/15 text-violet-400 flex items-center justify-center mx-auto">
                    <Mail className="w-5 h-5" />
                </div>
                <DialogHeader className="text-center items-center">
                    <DialogTitle className="text-white text-lg">¿Todavía no terminás?</DialogTitle>
                    <DialogDescription className="text-slate-400 text-center">
                        Dejanos tu correo y te ayudamos a completar tu cuenta cuando tengas un momento — no perdés nada de lo que ya empezaste.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3 text-left">
                    <label htmlFor="recovery-email" className="sr-only">Correo electrónico</label>
                    <Input
                        id="recovery-email"
                        data-testid="recovery-email"
                        type="email"
                        placeholder="tu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                        className="bg-slate-950/70 border-slate-700 h-11 rounded-xl text-white placeholder:text-slate-400"
                    />
                    <Button
                        type="submit"
                        data-testid="recovery-submit"
                        disabled={loading}
                        className="w-full h-11 bg-violet-500 hover:bg-violet-600 text-on-brand rounded-xl font-semibold"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Quiero que me ayuden'}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
